import * as React from 'react';
import axios from 'axios';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import { Alert } from '@material-ui/lab';
import { Box, Button, styled, TextField, Typography } from '@material-ui/core';
import { DiscountCodeErrorKey } from '../../types/enums';
import { get } from 'lodash';
import { IDiscount } from '../../types/discount';
import { Model, Question } from 'survey-core';
import { SurveyModel } from 'survey-core/survey.core';
import { SurveyQuestionElementBase } from 'survey-react-ui';
import { ValueChangedEvent } from 'survey-core/typings/survey-events-api';
import { OperationType } from '../../types/companyDetails';

export const TYPE = 'mui-discount';

export class MuiDiscountModel extends Question {
  getType(): string {
    return TYPE;
  }

  get validatorUrl() {
    return this.getPropertyValue('validatorUrl');
  }
  set validatorUrl(val) {
    this.setPropertyValue('validatorUrl', val);
  }

  get strings() {
    return this.getPropertyValue('strings');
  }
  set strings(val) {
    this.setPropertyValue('strings', val);
  }
}

const StyledTypography = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(-0.25),
  '& span': {
    fontWeight: theme.typography.fontWeightMedium
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: 274,
  [theme.breakpoints.down('md')]: {
    width: '100%',
    marginBottom: theme.spacing(1.25)
  },
  '& input': {
    textTransform: 'uppercase'
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  border: `1px solid var(--light-outline)`,
  borderRadius: '100px',
  textTransform: 'unset',
  width: '87px',
  marginTop: theme.spacing(1),
  '&:disabled': {
    backgroundColor: 'var(--disabled)',
    borderColor: 'var(--disabled)'
  },
  '&:hover': {
    borderColor: 'var(--light-outline)'
  }
}));

const StyledAlert = styled(Alert)(({ theme }) => ({
  backgroundColor: 'var(--medium-gray)',
  maxWidth: '378px',
  height: '64px',
  boxSizing: 'border-box',
  borderRadius: '12px',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    marginTop: theme.spacing(2)
  },
  '& .MuiAlert-icon': {
    fontSize: '2.5rem',
    [theme.breakpoints.down('md')]: {
      fontSize: '1.5rem'
    }
  },
  '& .MuiAlert-message': {
    color: 'var(--black-text)',
    '& p': {
      [theme.breakpoints.down('md')]: {
        fontSize: 14
      }
    },
    '& p span': {
      fontWeight: 600
    }
  },
  '& .MuiButton-root': {
    textTransform: 'none',
    fontWeight: 600
  }
}));

const getErrorKeyByCode = (code: DiscountCodeErrorKey) =>
  ({
    [DiscountCodeErrorKey.CODE_NOT_FOUND]: 'codeNotFound',
    [DiscountCodeErrorKey.CODE_EXPIRED]: 'codeExpired',
    [DiscountCodeErrorKey.CODE_CANNOT_BE_APPLIED]: 'codeCannotByApplied',
    [DiscountCodeErrorKey.CLIENT_NOT_FOUND]: 'clientNotFound'
  }[code] || 'unknownError');

const STORED_DISCOUNT_DATA = 'signup:storedDiscountData';

export class MuiDiscount extends SurveyQuestionElementBase {
  constructor(props: any) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleApply = this.handleApply.bind(this);
    this.handleRemove = this.handleRemove.bind(this);
    this.valueChangedHandler = this.valueChangedHandler.bind(this);
    this.checkDiscountActive();
  }

  componentDidMount() {
    super.componentDidMount();
    (this.question.survey as Model).onValueChanged.add(this.valueChangedHandler);
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    const model = this.question.survey as Model;

    model.setVariable('discountParams', JSON.stringify(this.getRequestBody()));
    model.onValueChanged.remove(this.valueChangedHandler);
  }

  protected valueChangedHandler(model: SurveyModel, options: ValueChangedEvent) {
    const subscriptionChanged = options.name === 'subscription' && !!options.value;
    const additionalProductChanged = options.name === 'additionalProduct' && !!options.value;

    if (subscriptionChanged) {
      this.handleRemove();
      return;
    }

    // Need to reflect discount changes when switching between support packages if the discount is applied to each item
    if (additionalProductChanged) {
      if (this.question.errors.length > 0) {
        this.question.clearErrors();
      }
      model.currentPage.setPropertyValue('forceUpdated', Date.now());
    }
  }

  checkDiscountActive() {
    const model = this.question.survey as Model;
    const prevDiscountParams = model.getVariable('discountParams');

    if (!!prevDiscountParams && prevDiscountParams !== JSON.stringify(this.getRequestBody())) {
      this.handleRemove();
    }
  }

  get question() {
    return this.questionBase;
  }

  get validatorUrl() {
    return this.question.validatorUrl;
  }

  getString(key: string) {
    return get(this.question, ['strings', key], key);
  }

  handleChange({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
    if (value === ' ') return;
    this.question.value = value;
  }

  handleBlur({ target: { value } }: React.FocusEvent<HTMLInputElement>) {
    this.question.value = value ? value.trim() : value;
  }

  getRequestBody() {
    const model = this.question.survey as Model;
    const filteredProducts = model.getVariable('filteredProducts') || {};
    const applicationType =
      model.data['applicationType'] || model.runExpression('expandChoicesByUrl({subscription}, applicationType)');
    const assessmentType =
      model.data['assessmentType'] ||
      model.runExpression('expandChoicesByUrl({subscription}, assessmentType)')?.replace('CHAS ', '');
    const selectedProducts = [
      model.runExpression('expandChoicesByUrl({subscription}, id)'),
      model.runExpression("getJoiningFee('id')")
    ];

    if (Array.isArray(filteredProducts.additionalProduct)) {
      selectedProducts.push(...filteredProducts.additionalProduct);
    }

    return {
      location: model.data['packageType'],
      applicationType,
      assessmentType,
      subscriptionTier: model.runExpression('expandChoicesByUrl({subscription}, subscriptionTier)'),
      operationType: OperationType.SIGNUP,
      selectedProducts: selectedProducts.filter(value => !!value)
    };
  }

  handleApply() {
    const model = this.question.survey as Model;
    const type = model.getVariable('signupType');

    if (this.validatorUrl) {
      const body = {
        code: this.question.value,
        ...this.getRequestBody()
      };
      this.question.setPropertyValue('codeIsChecking', true);
      axios
        .post<IDiscount>(this.validatorUrl, body)
        .then(response => {
          const discount = response.data;

          model.setVariable('discount', discount);
          this.question.setPropertyValue('appliedCode', this.question.value);
          sessionStorage.setItem(`${STORED_DISCOUNT_DATA}_${type}`, JSON.stringify(discount));
          model.currentPage.setPropertyValue('forceUpdated', Date.now());
        })
        .catch(error => {
          if (error.response.status === 406) {
            this.question.addError(this.getString(getErrorKeyByCode(get(error.response, ['data'], ''))));
            return;
          }
          this.question.addError(this.getString('unknownError'));
        })
        .finally(() => {
          this.question.setPropertyValue('codeIsChecking', false);
        });
    }
  }

  handleRemove() {
    const model = this.question.survey as Model;
    const type = model.getVariable('signupType');

    model.setVariable('discount', null);
    this.question.setPropertyValue('appliedCode', null);
    this.question.clearValue();
    this.question.clearErrors();
    sessionStorage.removeItem(`${STORED_DISCOUNT_DATA}_${type}`);
    model.currentPage.setPropertyValue('forceUpdated', Date.now());
  }

  protected renderElement(): JSX.Element | null {
    const { value = '', title, isRequired, errors, isReadOnly } = this.question;
    const appliedCode = this.question.getPropertyValue('appliedCode');
    const model = this.question.survey as Model;
    const codeIsChecking = this.question.getPropertyValue('codeIsChecking');
    const relativeDiscountAmount = model.calculatedValues.find(
      calculatedValue => calculatedValue.name === 'relativeDiscountAmount'
    );
    const discount = model.getVariable('discount');

    if (appliedCode) {
      return (
        <StyledAlert
          severity="info"
          icon={<LocalOfferIcon color="primary" fontSize="inherit" />}
          action={
            <Button color="primary" size="small" onClick={this.handleRemove}>
              {this.getString('remove')}
            </Button>
          }
        >
          <StyledTypography>
            <span>{appliedCode}</span> {this.getString('codeApplied')}
          </StyledTypography>
          {discount?.amount > 0 && relativeDiscountAmount && (
            <Typography variant="caption">-{relativeDiscountAmount.value}</Typography>
          )}
        </StyledAlert>
      );
    }

    return (
      <Box display="flex" alignItems="start" mt={2}>
        <StyledTextField
          label={title}
          value={value}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          required={isRequired}
          variant="outlined"
          error={errors.length > 0}
          helperText={errors[0] ? errors[0].locText.text : ''}
          InputProps={{
            readOnly: this.isDisplayMode
          }}
          inputProps={{
            tabIndex: 1
          }}
          style={{ width: '274px' }}
          disabled={isReadOnly}
        />
        <Box flexShrink={0} marginTop={0.25} marginLeft={2}>
          <StyledButton
            variant="outlined"
            color="primary"
            tabIndex={1}
            onClick={this.handleApply}
            disabled={codeIsChecking || !value || isReadOnly}
          >
            {this.getString('apply')}
          </StyledButton>
        </Box>
      </Box>
    );
  }
}
